import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { apiFetch } from '../../services/apiClient';
import { CreatePoModal } from './CreatePoModal';
import { EquipmentManager } from './AdminEquipment';
import { DriverInfoContent } from './AdminDriverInfo';
import { ShopOrdersBoard } from './AdminShopOrders';
import { MaterialHub } from './AdminMaterial';
import ActivityPills from '../../components/ActivityPills';
import PreviewRoleContext from '../../context/PreviewRoleContext';

// The Shop page is a card hub (like Settings / Workflows). Safety used to be the first
// tile here; it is its own page in the side menu now, right under Shop (Pat, Sep 2026)
// — /admin/safety. An old link to ?tab=safety is forwarded there.
const TABS = [
  { key: 'shop_order', label: 'Shop Order',       icon: '📦', color: '#1e3a5f', sub: 'Deliveries & prefab' },
  { key: 'equipment',  label: 'Equipment',         icon: '🔧', color: '#475569', sub: 'Fleet & rentals' },
  { key: 'driver',     label: 'Driver Tools', icon: '🚚', color: '#b45309', sub: 'Fuel, inspections, incidents' },
  { key: 'material',   label: 'Material',          icon: '🧱', color: '#065f46', sub: 'Received materials' },
];

// The office (Accounting role) and Executives — and the owner login — add, edit and delete
// equipment from the Shop page; everyone else who reaches it (Project Managers) gets the
// view-and-maintenance version (Pat, Sep 2026 — it used to be locked for everybody here).
// The server gate is wider (OFFICE_ROLES); this is only which screen you get. Desktop
// Preview shows the previewed role's version.
const EQUIPMENT_FULL_ROLES = ['admin', 'Accounting', 'OfficeStaff', 'Executive'];
function sessionRole() {
  try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.role || 'admin'; } catch { return 'admin'; }
}

export default function AdminShop() {
  const [searchParams] = useSearchParams();
  const previewRole = useContext(PreviewRoleContext);
  const equipmentFull = EQUIPMENT_FULL_ROLES.includes(previewRole || sessionRole());
  // Deep-link support: /admin/shop?tab=shop_order&order=<id> opens a section
  // (and a specific shop order) directly — used after writing a PO.
  const [tab, setTab] = useState(searchParams.get('tab') || null);
  const openOrderId = searchParams.get('order');
  const openEquipmentId = searchParams.get('equipment'); // /admin/shop?tab=equipment&equipment=<id> opens that machine's card
  const active = TABS.find(t => t.key === tab);
  const navigate = useNavigate();
  useEffect(() => { if (tab === 'safety') navigate('/admin/safety', { replace: true }); }, [tab, navigate]);

  // "Write PO" for the shop — resolve the Shop job (division 'shop' / #2699) + vendors.
  const [shopProject, setShopProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [showPo, setShowPo] = useState(false);
  const [poMsg, setPoMsg] = useState('');
  useEffect(() => {
    apiFetch('/projects').then(list => {
      const arr = Array.isArray(list) ? list : [];
      setProjects(arr);
      const nameOnly = n => (n || '').replace(/^\s*\d+\s*-\s*/, '').trim().toLowerCase();
      const shop = arr.find(p => p.division === 'shop')
        || arr.find(p => String(p.project_number) === '2699')
        || arr.find(p => nameOnly(p.name) === 'shop');
      setShopProject(shop || null);
    }).catch(() => {});
    apiFetch('/vendors/active').then(v => setVendors(Array.isArray(v) ? v : [])).catch(() => {});
  }, []);

  const poModal = (
    <CreatePoModal open={showPo} onClose={() => setShowPo(false)}
      projectId={shopProject?.id} projectLabel={shopProject?.name || 'Shop'} projects={projects} vendors={vendors}
      onCreated={po => { setPoMsg(`✓ PO #${po.po_number} written`); setTimeout(() => setPoMsg(''), 6000); }} />
  );

  // ── Card hub ─────────────────────────────────────────────────────────────
  if (!active) {
    return (
      <AdminLayout>
        <div className="admin-page-header">
          <div>
            <h1 className="admin-page-title">Shop</h1>
            <p className="admin-page-sub">Orders, equipment, drivers &amp; materials</p>
            <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <ActivityPills pageName="Shop" scope="shop" summaryLabel="Shop Update" />
              <button onClick={() => { if (shopProject) setShowPo(true); else alert('Shop job not found — make sure a "2699 - Shop" job exists.'); }}
                title="Write a purchase order charged to the Shop"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#1e3a5f', color: '#fff', border: 'none', borderRadius: 999, padding: '7px 15px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                🧾 Write PO
              </button>
            </div>
            {poMsg && <div style={{ marginTop: 8, fontSize: 13, fontWeight: 700, color: '#15803d' }}>{poMsg}</div>}
          </div>
        </div>
        {poModal}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{ textAlign: 'left', background: '#fff', border: '1px solid var(--border)', borderTop: `4px solid ${t.color}`, borderRadius: 12, padding: 20, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 8, transition: 'box-shadow .15s, transform .1s' }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}>
              <div style={{ fontSize: 30 }}>{t.icon}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{t.label}</div>
              <div style={{ fontSize: 13, color: t.color, fontWeight: 600 }}>{t.sub}</div>
            </button>
          ))}
        </div>
      </AdminLayout>
    );
  }

  // ── Single section ───────────────────────────────────────────────────────
  return (
    <AdminLayout>
      <button onClick={() => setTab(null)}
        style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
        ‹ Shop
      </button>

      {tab === 'equipment' && <EquipmentManager restricted={!equipmentFull} embedded openEquipmentId={openEquipmentId} />}
      {tab === 'shop_order' && <ShopOrdersBoard openOrderId={openOrderId} />}
      {tab === 'driver' && <DriverInfoContent />}
      {tab === 'material' && <MaterialHub />}
    </AdminLayout>
  );
}
